import { CommonApi, OrderApi, PeopleApi, ReceiptApi } from '@/api';
import { SupportHint, Textarea } from '@/components';
import { useAuthContext, useLegalMutationsAllowed, useProfile } from '@/hooks';
import type { AddressResponse } from '@/model/Address.ts';
import { formatMedicinePlural, formatMoney, ruToIsoDate } from '@/utils';
import classNames from 'classnames';

import TrashIcon from '../../../assets/icons/trash.svg?react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { IMaskInput } from 'react-imask';
import { toast } from 'react-toastify';

import { AddMedicineModal } from './add-medicine-modal';
import { CreateAddressModal } from './create-address-modal';
import { MedicineCard } from './medicine-card';
import {
  ErrorBlock,
  type IntakeMethod,
  PlusIcon,
  WarnIcon,
  getPartOfDay,
} from './medicine-fields';
import type { OrderMedicine } from './order-medicine';
import { buildPreviewItems, mapPreviewToOrderPreview } from './order-preview';

const CameraIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
      stroke="#2c9aee"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="13" r="4" stroke="#2c9aee" strokeWidth="1.5" />
  </svg>
);

const FileIcon = ({ className = '' }: { className?: string }) => (
  <span className="flex size-[36px] items-center justify-center rounded-[50%] bg-[#e4eff8] p-[8px]">
    <svg
      className={classNames(className, 'relative left-[1px]')}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.625 17.5H4.375C4.20924 17.5 4.05027 17.4342 3.93306 17.3169C3.81585 17.1997 3.75 17.0408 3.75 16.875V3.125C3.75 2.95924 3.81585 2.80027 3.93306 2.68306C4.05027 2.56585 4.20924 2.5 4.375 2.5H11.875L16.25 6.875V16.875C16.25 17.0408 16.1842 17.1997 16.0669 17.3169C15.9497 17.4342 15.7908 17.5 15.625 17.5Z"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.875 2.5V6.875H16.25"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

type UploadedFileItemProps = {
  fileName: string;
  onDelete: () => void;
};

const UploadedFileItem = ({ fileName, onDelete }: UploadedFileItemProps) => (
  <div className="flex h-14 w-full items-center justify-center gap-[12px] rounded-[12px] bg-[#f7f8f9] p-[16px]">
    <FileIcon />
    <div className="flex-1 text-base leading-5 text-[#151515]">{fileName}</div>
    <button type="button" onClick={onDelete}>
      <TrashIcon className="size-[20px]" />
    </button>
  </div>
);

const GosuslugiIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="24" rx="4" fill="#0d4cd3" />
    <path d="M12 7L16 11H13V17H11V11H8L12 7Z" fill="white" />
  </svg>
);

const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#5fd98e" />
    <path
      d="M8 12L11 15L16 9"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Кнопка «назад» в футере шага. Одна на все шаги — на шаге «Заказ» стрелка
 * была нарисована в другом размере и выглядела заметно мельче остальных. */
const BackStepButton = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} type="button" className="relative flex-[0_0_auto]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
    >
      <rect width="60" height="60" rx="12" fill="#0788F1" fillOpacity="0.08" />
      <path
        d="M37.625 30H23.875"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M28.625 24.375L23 30L28.625 35.625"
        stroke="#0788F1"
        strokeWidth="1.6"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const LineIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="100%"
    height="1"
    viewBox="0 0 100 1"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="none"
  >
    <line x1="0" y1="0.5" x2="100" y2="0.5" stroke="#afc4d1" strokeWidth="1" />
  </svg>
);

/** Иконки для шага «Назначение» — stroke через currentColor (цвет рамки кнопки: покой #f1f7fa, актив/наведение #0788f1) */
const TreatIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="41"
    height="42"
    viewBox="0 0 41 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M1 25H14C16 25 18 28 20 28C21.7286 28 24.9514 26.5059 26.4398 29.3289C26.9102 30.2212 26.2819 31.2406 25.3502 31.6269C23.1921 32.5217 20.7883 33.7372 20 34L14 32M27 30L36.152 26.3392C36.7076 26.117 37.3118 26.0445 37.9042 26.1292C39.9977 26.4282 40.5891 29.1705 38.805 30.3059L23.5172 40.0345C22.5523 40.6486 21.3706 40.8202 20.2708 40.506L1 35M15 11V16C15 18.7614 17.2386 21 20 21C22.7614 21 25 18.7614 25 16V11M15 11V6C15 3.23858 17.2386 1 20 1C22.7614 1 25 3.23858 25 6V11M15 11H25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DoctorIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M17 12H31M20 34H28M24 38V30M30 22.3473C34.7299 24.5942 38 29.4152 38 35V39C38 41.2091 36.2091 43 34 43H14C11.7909 43 10 41.2091 10 39V35C10 29.4152 13.2701 24.5942 18 22.3473M24 24C20.134 24 17 20.866 17 17V8C17 5.79086 18.7909 4 21 4H27C29.2091 4 31 5.79086 31 8V17C31 20.866 27.866 24 24 24Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BandageIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="45"
    height="45"
    viewBox="0 0 45 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M29.7497 8.99575L29.7497 4.83333C29.7497 2.71624 28.0335 1 25.9164 1L18.2497 1C16.1326 1 14.4164 2.71624 14.4164 4.83333L14.4164 8.99541M29.7497 35.171L29.7497 39.3333C29.7497 41.4504 28.0335 43.1667 25.9164 43.1667H18.2497C16.1326 43.1667 14.4164 41.4504 14.4164 39.3333L14.4164 35.1714M29.75 14.4165L29.75 29.7498M14.4167 14.4165L14.4167 29.7498M1 18.2498L1 25.9165C1 28.0336 2.71624 29.7498 4.83333 29.7498L39.3333 29.7498C41.4504 29.7498 43.1667 28.0336 43.1667 25.9165V18.2498C43.1667 16.1328 41.4504 14.4165 39.3333 14.4165H4.83333C2.71624 14.4165 1 16.1328 1 18.2498Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Круглая иконка-подсказка в пустом состоянии списка лекарств. */
const InfoCircleIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M12 11v5.5M12 7.75h.01"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Синяя кнопка «Добавить лекарство» — открывает модалку схемы приёма. */
const AddMedicineButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1 rounded-lg bg-[#0788f1] py-1 pl-2 pr-3"
  >
    <span className="flex items-center justify-center p-1.5 text-white">
      <PlusIcon className="size-4" />
    </span>
    <span className="text-sm font-medium leading-5 text-white">
      Добавить лекарство
    </span>
  </button>
);

/**
 * Приёмы словами: «Утром», «Утром, вечером». Справочник времён отдаёт часы
 * («08:00»), а в «Итоге» их читают как часть суток. Если справочник отдал
 * название, а не время, оставляем его как есть.
 */
const formatIntakeTimes = (times: Array<{ value: string }>): string =>
  [
    ...new Set(
      times.map((time) =>
        /^\d/.test(String(time.value)) ? getPartOfDay(time.value) : time.value,
      ),
    ),
  ]
    .map((label, index) => (index === 0 ? label : label.toLowerCase()))
    .join(', ');

/** Строка блока итогов: «Доставка», «Сборка заказа», «Итого». */
const TotalRow = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="flex w-full items-center justify-between gap-[16px]">
    <div
      className={classNames(
        'text-sm leading-5 md:text-base',
        strong ? 'font-bold text-[#151515]' : 'text-[#151515]/50',
      )}
    >
      {label}
    </div>
    <div
      className={classNames(
        'text-right text-sm leading-5 md:text-base',
        strong ? 'font-bold text-[#151515]' : 'text-[#151515]',
      )}
    >
      {value}
    </div>
  </div>
);

type StepKey = 'purpose' | 'type' | 'extra' | 'summary' | 'data';

type WizardState = {
  hasPrescription: 'yes' | 'no' | 'unknown';
  prescriptionType: 'electronic' | 'paper' | 'select_from_list';
  orderFormationType?: 'self' | 'specialist';
  prescriptionFiles?: { id: number; fileName: string }[];
  /** Только для сценария с рецептом врача: докидывать ли своё сверх назначенного. */
  addMore: 'yes' | 'no';
  additionalMedicines: OrderMedicine[];
  description: string;
  orderComment?: string;
  missingItemsDescription?: string;
  missingMedicineDescription?: string;
  recipient: 'self' | 'other';
  recipientData?: {
    surname: string;
    name: string;
    midname?: string;
    birthdate?: string;
    email?: string;
  };
  paymentMethodId?: number;
  addressId?: number;
  freeFormReceiptId?: number; // ID созданного free-form рецепта
};

type Props = {
  /** orderId — созданный заказ, чтобы сразу открыть его карточку. */
  onComplete: (orderId?: number) => void;
  onBack: () => void;
};

const STEP_TITLES: Record<StepKey, string> = {
  purpose: 'Назначение',
  type: 'Тип',
  extra: 'Заказ',
  summary: 'Итог',
  data: 'Ваши данные',
};

const getStepTitle = (key: StepKey): string => STEP_TITLES[key];

const stepOrder: StepKey[] = ['purpose', 'type', 'extra', 'summary', 'data'];

/**
 * Шаги, которые сценарий реально пройдёт. Пропущенные не рендерим вовсе, чтобы
 * в шапке не оставалось вкладок, на которые пользователь никогда не попадёт.
 */
const getVisibleSteps = (
  hasPrescription?: 'yes' | 'no' | 'unknown',
): StepKey[] => {
  // «Нужна помощь»: с «Типа» уходим сразу в оформление — «Заказа» и «Итога» не будет.
  if (hasPrescription === 'unknown') return ['purpose', 'type', 'data'];
  // «Назначения нет»: собираем заказ руками, шага «Тип» не будет.
  if (hasPrescription === 'no') return ['purpose', 'extra', 'summary', 'data'];
  // «Есть назначение врача»: на «Заказе» можно докинуть своё к назначенному.
  return stepOrder;
};

type PropsWithUserId = Props & {
  userId?: number | null;
};

export const CreateOrderWizard = ({
  onComplete,
  onBack,
  userId,
}: PropsWithUserId) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingPrescriptionFileNameRef = useRef<string | null>(null);
  const profile = useProfile(true);
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();
  // Профиль подгружается асинхронно: пока его нет, идентификатор берём из
  // контекста авторизации — иначе загрузка файла уходила без patient_id и
  // бэкенд отвечал 400 («Ошибка загрузки файла»).
  const { userId: authUserId } = useAuthContext();
  const patientId = profile?.people_main_id ?? userId ?? authUserId ?? null;
  const queryClient = useQueryClient();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [active, setActive] = useState<StepKey>('purpose');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [wizardState, setWizardState] = useState<WizardState>({
    hasPrescription: 'yes',
    prescriptionType: 'paper',
    prescriptionFiles: [],
    addMore: 'no',
    additionalMedicines: [],
    description: '',
    missingItemsDescription: undefined,
    missingMedicineDescription: undefined,
    recipient: 'self',
    orderFormationType: 'self',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [freeFormError, setFreeFormError] = useState<string | null>(null);
  const [hasAttemptedExtra, setHasAttemptedExtra] = useState(false);
  /** Открытая модалка препарата: index — редактируем, null — добавляем новый. */
  const [medicineModal, setMedicineModal] = useState<{
    index: number | null;
  } | null>(null);
  const wizardCardRef = useRef<HTMLDivElement>(null);
  const stepsHeaderRef = useRef<HTMLDivElement>(null);

  const prevActiveStepRef = useRef(active);
  useEffect(() => {
    if (prevActiveStepRef.current === active) return;
    prevActiveStepRef.current = active;
    wizardCardRef.current?.scrollIntoView({
      block: 'start',
      inline: 'nearest',
      behavior: 'smooth',
    });
    stepsHeaderRef.current
      ?.querySelector<HTMLElement>('[aria-current="step"]')
      ?.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
  }, [active]);
  const { data: paymentList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 206],
    queryFn: () => CommonApi.getReference(206),
  });

  // По умолчанию всегда Юкасса (способ оплаты не выбирается пользователем)
  const defaultPaymentMethodId =
    paymentList.find((p) => /юкасса|yookassa/i.test(p.value))?.id ??
    paymentList[0]?.id;

  // «До/после/во время еды» — справочник 205 (как в докторской форме).
  const { data: eatingList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 205],
    queryFn: () => CommonApi.getReference(205),
  });

  // Способ приёма в визарде хранится строкой ('before'|'after'|'with'),
  // а в receipt_items бэкенд ждёт id из справочника 205 — маппим по code,
  // затем по названию; если справочник недоступен, поле не отправляем.
  const resolveEatingId = useCallback(
    (method?: IntakeMethod): number | undefined => {
      const m = method ?? 'before';
      // На тесте третье значение справочника — «В любое время», а не «Во время еды».
      const valuePatterns: Record<IntakeMethod, RegExp> = {
        before: /^до\s/i,
        after: /^после\s/i,
        with: /во\s*время|любое/i,
      };
      return (
        eatingList.find((e) => e.code === m)?.id ??
        eatingList.find((e) => valuePatterns[m].test(e.value))?.id ??
        eatingList[0]?.id
      );
    },
    [eatingList],
  );

  /** Позиции для расчёта — те же, что уйдут в receipt_items при создании заказа. */
  const previewItems = useMemo(
    () => buildPreviewItems(wizardState.additionalMedicines, resolveEatingId),
    [wizardState.additionalMedicines, resolveEatingId],
  );

  // Цены считает бэкенд: он знает упаковки, наценку, доставку и сборку.
  // Запрашиваем только на «Итоге» — на других шагах расчёт не показывается.
  const {
    data: previewResponse,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
  } = useQuery({
    queryKey: [ReceiptApi.KEY, 'preview', previewItems],
    queryFn: () => ReceiptApi.preview({ receipt_items: previewItems }),
    enabled: active === 'summary' && previewItems.length > 0,
    keepPreviousData: true,
  });

  const orderPreview = useMemo(
    () =>
      previewResponse
        ? mapPreviewToOrderPreview(
            wizardState.additionalMedicines,
            previewResponse,
          )
        : null,
    [previewResponse, wizardState.additionalMedicines],
  );

  const { data: addresses = { items: [] }, refetch: refetchAddresses } =
    useQuery({
      queryKey: [PeopleApi.KEY, 'getAddress', profile?.people_main_id],
      queryFn: () => PeopleApi.getAddress(profile?.people_main_id),
      enabled: !!profile?.people_main_id,
    });

  const handleAddressSelect = useCallback(
    (address: AddressResponse) => {
      setWizardState((prev) => ({
        ...prev,
        addressId: address.id,
      }));
      refetchAddresses();
    },
    [refetchAddresses],
  );

  const { mutate: createFreeFormReceipt, isLoading: isCreatingFreeForm } =
    useMutation({
      mutationFn: (params: {
        patient_id: number | null;
        text: string;
        scanned_receipts?: number[];
      }) => ReceiptApi.freeFormCreate(params),
      onSuccess: (response) => {
        const receiptId =
          response?.id ??
          (response as { receipt_id?: number } | undefined)?.receipt_id;
        if (!receiptId) {
          setFreeFormError(
            'Не удалось создать заявку. Попробуйте ещё раз или обратитесь в поддержку.',
          );
          return;
        }
        setFreeFormError(null);
        // Сохраняем ID free-form рецепта для создания заказа на шаге 'data'
        setWizardState((prev) => ({
          ...prev,
          freeFormReceiptId: receiptId,
        }));
        // Если адрес уже выбран, сразу создаем заказ
        if (wizardState.addressId) {
          confirmOrder({
            receiptId,
            addressId: wizardState.addressId,
            receiptType: 'free_form_receipt',
          });
        } else {
          // Иначе переходим на шаг выбора адреса
          setActive('data');
        }
      },
      onError: () => {
        setFreeFormError(
          'Не удалось отправить описание. Проверьте соединение и попробуйте ещё раз.',
        );
      },
    });

  const { mutate: createReceipt, isLoading: isCreatingReceipt } = useMutation({
    mutationFn: (params: {
      patient_id: number | null;
      address: number;
      description: string;
      payment_method: number;
      analysis_result: Array<{
        message: string;
        level: 'info' | 'warning' | 'danger';
      }>;
      scanned_receipts?: number[];
      recipient: {
        surname: string;
        name: string;
        midname?: string;
        birthdate?: string;
        email?: string;
      };
      receipt_items: Array<{
        amount: number;
        duration: number;
        medicine_id: number;
        frequency_id: number;
        eating_id?: number;
        daytime_id: number;
        start: string;
      }>;
    }) => ReceiptApi.clientCreate(params),
    onSuccess: (response, params) => {
      // После создания рецепта создаем заказ (addressId из params, не из wizardState).
      // response может быть undefined: бэкенд отвечает 200 и при ошибке,
      // тогда в data.data пусто — это не успех.
      if (response?.receipt_id && params.address) {
        confirmOrder({
          receiptId: response.receipt_id,
          addressId: params.address,
          receiptType: 'receipt',
        });
      } else {
        setOrderSubmitted(false);
        toast.error('Не удалось оформить заказ. Попробуйте ещё раз.');
      }
    },
    onError: () => {
      setOrderSubmitted(false);
      toast.error('Не удалось оформить заказ. Попробуйте ещё раз.');
    },
  });

  const { mutate: confirmOrder, isLoading: isConfirmingOrder } = useMutation({
    mutationFn: ({
      receiptId,
      addressId,
      receiptType,
    }: {
      receiptId: number;
      addressId: number;
      receiptType?: 'receipt' | 'free_form_receipt';
    }) => OrderApi.confirm(receiptId, addressId, receiptType),
    onSuccess: async (response, variables) => {
      // Бэкенд может ответить 200 со status: 'error' в теле — раньше мы
      // молча уводили на «Текущие заказы», где заказа, естественно, не было.
      const body = response as
        | {
            status?: string;
            message?: string;
            error?: string;
            id?: number;
            order_id?: number;
            data?: { id?: number; order_id?: number };
          }
        | undefined;
      if (body?.status && body.status !== 'success') {
        setOrderSubmitted(false);
        toast.error(
          body.message ||
            body.error ||
            'Не удалось оформить заказ. Попробуйте ещё раз.',
        );
        return;
      }
      setOrderSubmitted(true);
      // Список заказов мог закэшироваться пустым до создания заказа.
      queryClient.invalidateQueries([OrderApi.KEY]);

      if (variables.receiptType === 'free_form_receipt') {
        // По свободной заявке заказа нет — говорим об этом прямо, иначе
        // пользователь ищет его в «Текущих заказах» и не находит.
        toast.success(
          'Заявка отправлена. Менеджер свяжется с вами для уточнения деталей.',
        );
        onComplete();
        return;
      }
      // Открываем созданный заказ. Id берём из ответа, а если бэкенд его не
      // вернул — ищем заказ по receipt_id в свежих списках (он есть в обоих).
      let createdOrderId =
        body?.data?.id ?? body?.data?.order_id ?? body?.id ?? body?.order_id;
      if (!createdOrderId) {
        try {
          const [active, notPaid] = await Promise.all([
            queryClient.fetchQuery({
              queryKey: [OrderApi.KEY, 'getActive'],
              queryFn: OrderApi.getActive,
            }),
            queryClient.fetchQuery({
              queryKey: [OrderApi.KEY, 'getNotPaid'],
              queryFn: OrderApi.getNotPaid,
            }),
          ]);
          createdOrderId = [...notPaid, ...active].find(
            (order) => order.receipt_id === variables.receiptId,
          )?.id;
        } catch {
          // Не нашли — просто откроем список текущих заказов.
        }
      }
      onComplete(createdOrderId);
    },
    onError: () => {
      setOrderSubmitted(false);
      toast.error('Не удалось оформить заказ. Попробуйте ещё раз.');
    },
  });

  const validateStep = useCallback(
    (step: StepKey) => {
      const newErrors: Record<string, string> = {};

      if (step === 'purpose') {
        if (
          wizardState.hasPrescription !== 'yes' &&
          wizardState.hasPrescription !== 'no' &&
          wizardState.hasPrescription !== 'unknown'
        ) {
          newErrors.hasPrescription =
            'Выберите, есть ли у вас назначение врача';
        }
      }

      if (step === 'type') {
        if (wizardState.hasPrescription === 'yes') {
          if (
            wizardState.prescriptionType === 'electronic' ||
            wizardState.prescriptionType === 'paper'
          ) {
            if (!wizardState.prescriptionFiles?.length) {
              newErrors.prescriptionImage = 'Загрузите фото рецепта';
            }
          }
        } else if (wizardState.hasPrescription === 'no') {
          if (wizardState.prescriptionType === 'paper') {
            if (!wizardState.prescriptionFiles?.length) {
              newErrors.prescriptionImage = 'Загрузите фото рецепта';
            }
          }
        } else if (wizardState.hasPrescription === 'unknown') {
          if (!wizardState.description.trim()) {
            newErrors.description = 'Опишите назначение';
          }
        }
      }

      if (step === 'extra') {
        // В сценарии с рецептом врача список может остаться пустым — там это
        // добавка сверх назначенного. Без рецепта нужно хоть что-то: лекарство
        // из списка, прикреплённый файл или описание в «Нет моего лекарства».
        if (
          wizardState.hasPrescription === 'no' &&
          wizardState.additionalMedicines.length === 0 &&
          !wizardState.prescriptionFiles?.length &&
          !wizardState.missingMedicineDescription?.trim()
        ) {
          newErrors.additionalMedicines =
            'Добавьте лекарство или прикрепите файл со списком';
        }
      }

      if (step === 'summary') {
        if (
          wizardState.hasPrescription === 'yes' &&
          wizardState.additionalMedicines.length === 0 &&
          !wizardState.prescriptionFiles?.length
        ) {
          newErrors.summary = 'Добавьте лекарства или загрузите рецепт';
        }
        if (
          wizardState.hasPrescription === 'no' &&
          wizardState.orderFormationType === 'self' &&
          wizardState.additionalMedicines.length === 0 &&
          !wizardState.prescriptionFiles?.length &&
          !wizardState.missingMedicineDescription?.trim()
        ) {
          newErrors.summary =
            'Добавьте лекарство или прикрепите файл со списком';
        }
      }

      if (step === 'data') {
        if (!wizardState.addressId) {
          newErrors.addressId = 'Выберите адрес доставки';
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [wizardState],
  );

  /**
   * Единственный способ уйти вперёд: строго на следующий видимый шаг и только
   * если текущий заполнен. Перепрыгнуть через шаг нельзя — вкладки в шапке
   * показывают прогресс, но не кликаются.
   */
  const goNext = useCallback(() => {
    if (!validateStep(active)) return;
    const visibleSteps = getVisibleSteps(wizardState.hasPrescription);
    const currentIdx = visibleSteps.indexOf(active);
    if (currentIdx < 0 || currentIdx === visibleSteps.length - 1) return;
    setActive(visibleSteps[currentIdx + 1]);
    setErrors({});
  }, [active, validateStep, wizardState.hasPrescription]);

  /** «Продолжить» на шаге «Заказ»: препараты уже сохранены модалкой. */
  const handleExtraNext = useCallback(() => {
    setHasAttemptedExtra(true);
    goNext();
  }, [goNext]);

  const handlePrevStep = useCallback(() => {
    setFreeFormError(null);
    const visibleSteps = getVisibleSteps(wizardState.hasPrescription);
    const currentIdx = visibleSteps.indexOf(active);
    if (currentIdx > 0) {
      setActive(visibleSteps[currentIdx - 1]);
    } else {
      onBack();
    }
  }, [active, onBack, wizardState.hasPrescription]);

  const { mutate: uploadDocument } = useMutation({
    mutationFn: (formData: FormData) => ReceiptApi.uploadDocument(formData),
    onSuccess: (response) => {
      // CommonApi.uploadDocument возвращает data из axios, который уже является ApiResponse
      // Структура ответа: { data: { document_id: number }, status: 'success' }
      // response уже является ApiResponse, поэтому response.data это { document_id: number }
      const documentId = response?.data?.document_id;

      // Отладочный вывод для проверки структуры
      console.log('Upload response:', response);
      console.log('Document ID:', documentId);

      if (documentId) {
        const fileName = pendingPrescriptionFileNameRef.current ?? 'Файл';
        pendingPrescriptionFileNameRef.current = null;
        setWizardState((prev) => ({
          ...prev,
          prescriptionFiles: [
            ...(prev.prescriptionFiles ?? []),
            { id: documentId, fileName },
          ],
        }));
        setIsUploading(false);
        setUploadError(null);
      } else {
        console.error('Не удалось получить document_id из ответа:', response);
        setUploadError('Не удалось получить ID загруженного файла');
        setIsUploading(false);
      }
    },
    onError: () => {
      // Только uploadError: он рисуется прямо под зоной загрузки. Дублировать
      // тот же текст в errors.prescriptionImage нельзя — там валидация шага
      // («Загрузите фото рецепта»), и в футере получалась вторая такая же плашка.
      setUploadError('Ошибка загрузки файла');
      setIsUploading(false);
    },
  });

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setUploadError(null);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.prescriptionImage;
        return newErrors;
      });
      pendingPrescriptionFileNameRef.current = file.name;
      setIsUploading(true);

      const formData = new FormData();
      formData.append('document', file);
      if (patientId) {
        formData.append('patient_id', String(patientId));
      }

      uploadDocument(formData);
    },
    [uploadDocument, patientId],
  );

  const handleFileUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCreateOrder = useCallback(() => {
    if (
      !profile?.people_main_id ||
      !wizardState.addressId ||
      !defaultPaymentMethodId
    ) {
      setOrderSubmitted(false);
      toast.error('Не удалось оформить заказ. Попробуйте ещё раз.');
      return;
    }

    const recipient =
      wizardState.recipient === 'self'
        ? {
            surname: profile.surname || '',
            name: profile.name || '',
            midname: profile.midname,
            birthdate: profile.birthdate,
            email: profile.email,
          }
        : wizardState.recipientData
        ? {
            surname: wizardState.recipientData.surname || '',
            name: wizardState.recipientData.name || '',
            midname: wizardState.recipientData.midname,
            birthdate:
              ruToIsoDate(wizardState.recipientData.birthdate || '') ??
              undefined,
            email: wizardState.recipientData.email,
          }
        : {
            surname: '',
            name: '',
          };

    const receiptData: {
      patient_id: number;
      address: number;
      description: string;
      payment_method: number;
      analysis_result: Array<{
        message: string;
        level: 'info' | 'warning' | 'danger';
      }>;
      scanned_receipts?: number[];
      recipient: {
        surname: string;
        name: string;
        midname?: string;
        birthdate?: string;
        email?: string;
      };
      receipt_items: Array<{
        amount: number;
        duration: number;
        medicine_id: number;
        frequency_id: number;
        eating_id?: number;
        daytime_id: number;
        start: string;
      }>;
    } = {
      patient_id: profile.people_main_id,
      address: wizardState.addressId,
      // Комментарий пользователя из «Итога» тоже уходит в описание рецепта —
      // раньше он никуда не отправлялся и терялся.
      description: [
        wizardState.missingItemsDescription ||
          wizardState.missingMedicineDescription ||
          wizardState.description ||
          '',
        wizardState.orderComment?.trim() || '',
      ]
        .filter(Boolean)
        .join('\n'),
      payment_method: defaultPaymentMethodId,
      analysis_result: [],
      recipient,
      receipt_items: wizardState.additionalMedicines.flatMap((medicine) =>
        medicine.time.map((time) => ({
          amount: Number(time.count),
          duration: medicine.duration,
          start: medicine.start,
          daytime_id: time.timeId,
          frequency_id: medicine.frequency.id,
          // Раньше при пустом eatingId сюда уходил time.timeId — id из
          // справочника времён (202), чужого для «до/после еды».
          eating_id: time.eatingId ?? resolveEatingId(medicine.intakeMethod),
          medicine_id: medicine.medicine.id,
        })),
      ),
    };

    // Добавляем scanned_receipts, если есть загруженные файлы
    if (wizardState.prescriptionFiles?.length) {
      receiptData.scanned_receipts = wizardState.prescriptionFiles.map(
        (f) => f.id,
      );
    }

    createReceipt(receiptData);
  }, [
    wizardState,
    profile,
    createReceipt,
    defaultPaymentMethodId,
    resolveEatingId,
  ]);

  /**
   * Список добавленных лекарств: пока пусто — объясняем, зачем этот шаг;
   * дальше карточки со схемой приёма и кнопка добавления. Саму схему
   * собирают в модалке, поэтому на шаге видно только результат.
   */
  const medicinesEditor = (
    <div className="flex w-full flex-col items-start gap-4">
      {wizardState.additionalMedicines.length === 0 ? (
        <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-[#deecf5] p-6">
          <InfoCircleIcon className="size-6 shrink-0 text-[#151515] opacity-40" />
          <p className="w-full max-w-[25rem] text-center text-base font-normal leading-6 text-[#151515]">
            Выберите необходимые вам лекарства и уточните схему их применения,
            чтобы мы сформировали удобную фасовку.
          </p>
          <AddMedicineButton
            onClick={() => setMedicineModal({ index: null })}
          />
        </div>
      ) : (
        <>
          {wizardState.additionalMedicines.map((medicine, index) => (
            <MedicineCard
              key={index}
              medicine={medicine}
              onEdit={() => setMedicineModal({ index })}
              onDelete={() =>
                setWizardState((prev) => ({
                  ...prev,
                  additionalMedicines: prev.additionalMedicines.filter(
                    (_, i) => i !== index,
                  ),
                }))
              }
            />
          ))}
          <AddMedicineButton
            onClick={() => setMedicineModal({ index: null })}
          />
        </>
      )}

      <button
        type="button"
        onClick={() =>
          setWizardState((prev) => ({
            ...prev,
            missingMedicineDescription:
              prev.missingMedicineDescription === undefined ? '' : undefined,
          }))
        }
        className="text-sm font-bold leading-5 text-[#0788f1]"
      >
        Нет моего лекарства
      </button>
      {wizardState.missingMedicineDescription !== undefined && (
        <input
          className="flex h-14 w-full items-center gap-3 rounded-xl border border-[#afc4d1] px-5 py-4 outline-none transition focus-within:border-[#0788f1]"
          placeholder="Укажите название лекарства, которое вы не нашли в списке"
          value={wizardState.missingMedicineDescription}
          onChange={(e) =>
            setWizardState((prev) => ({
              ...prev,
              missingMedicineDescription: e.target.value,
            }))
          }
        />
      )}

      {medicineModal && (
        <AddMedicineModal
          isOpen
          initial={
            medicineModal.index != null
              ? wizardState.additionalMedicines[medicineModal.index]
              : undefined
          }
          onClose={() => setMedicineModal(null)}
          onSave={(medicine) => {
            setWizardState((prev) => {
              const medicines = [...prev.additionalMedicines];
              if (medicineModal.index != null) {
                medicines[medicineModal.index] = medicine;
              } else {
                medicines.push(medicine);
              }
              return { ...prev, additionalMedicines: medicines };
            });
            setMedicineModal(null);
          }}
        />
      )}
    </div>
  );

  /** Зона загрузки фото + список загруженных файлов + ошибка загрузки.
   * Используется на шаге «Тип» (бумажный рецепт) и на шаге «Заказ»
   * в сценарии без назначения (файл вместо ручного выбора лекарств). */
  const renderUploadZone = (label: string) => (
    <div className="flex w-full flex-col items-start gap-[12px]">
      <div
        className="flex w-full cursor-pointer items-center justify-center gap-[12px] rounded-[12px] border border-dashed px-[20px] py-[32px]"
        style={{
          background: '#2c9aee14',
          borderColor: '#2c9aee80',
        }}
        onClick={handleFileUploadClick}
      >
        {isUploading ? (
          <svg
            className="size-[28px] animate-spin text-[#2c9aee]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <CameraIcon className="size-[28px]" />
        )}
        <div className="text-base leading-5 text-[#2c9aee]">
          {isUploading ? 'Загрузка...' : label}
        </div>
      </div>
      {wizardState.prescriptionFiles?.length ? (
        <div className="flex w-full flex-col gap-[8px]">
          {wizardState.prescriptionFiles.map((file) => (
            <UploadedFileItem
              key={file.id}
              fileName={file.fileName}
              onDelete={() => {
                setUploadError(null);
                setWizardState((prev) => ({
                  ...prev,
                  prescriptionFiles: (prev.prescriptionFiles ?? []).filter(
                    (f) => f.id !== file.id,
                  ),
                }));
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            />
          ))}
        </div>
      ) : null}
      {uploadError && <ErrorBlock message={uploadError} />}
    </div>
  );

  return (
    <div className="mt-[24px] min-h-[calc(100vh-24px)] overflow-y-auto rounded-[16px] bg-[#f1f7fa] px-[16px] pb-[24px] md:mt-0 md:max-w-[calc(100vw-250px)] md:p-9">
      <div
        ref={wizardCardRef}
        className="mx-auto flex min-h-[600px] w-full flex-col items-start rounded-[24px] bg-white md:w-[720px]"
      >
        <div className="flex w-full items-center border-b border-[#e4f0f7]">
          <div
            ref={stepsHeaderRef}
            className="flex w-full items-center overflow-x-auto scrollbar-hide"
          >
            {/* Индикатор прогресса, а не навигация: вперёд пускает только
                «Продолжить» после проверки текущего шага, назад — «Назад». */}
            {getVisibleSteps(wizardState.hasPrescription).map((key) => (
              <div
                key={key}
                aria-current={active === key ? 'step' : undefined}
                className={classNames(
                  'flex flex-1 cursor-default select-none items-center justify-center gap-[8px] whitespace-nowrap p-[16px]',
                  active === key && 'border-b-[0.125rem] border-[#0788f1]',
                )}
              >
                <div
                  className={classNames(
                    'text-base leading-5 text-[#151515]',
                    active !== key && 'opacity-50',
                  )}
                >
                  {getStepTitle(key)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {active === 'purpose' && (
          <div className="flex min-h-[606px] w-full flex-1 flex-col items-start justify-between self-stretch rounded-[24px] bg-white p-6 md:p-10">
            <div className="flex w-full flex-col items-start gap-6 md:gap-8">
              <div className="flex w-full flex-col items-start gap-8 md:gap-6">
                <div className="flex w-full flex-col items-start gap-2">
                  <p className="w-full text-center text-xl leading-7 text-[#151515] md:text-2xl md:leading-8">
                    Есть ли у Вас назначение врача?
                  </p>
                  <p className="w-full text-center text-sm leading-5 text-[#151515] opacity-50 md:text-base">
                    Выберите подходящий вариант – мы подстроим форму под вас.
                  </p>
                </div>

                <div className="mb-[30px] flex w-full flex-col items-start gap-4 md:mb-0">
                  <div className="flex w-full flex-col items-start gap-4 md:flex-row">
                    <button
                      type="button"
                      onClick={() =>
                        setWizardState((prev) => ({
                          ...prev,
                          hasPrescription: 'no',
                          prescriptionType: 'select_from_list',
                          orderFormationType: 'self',
                        }))
                      }
                      className={`group flex w-full flex-1 items-center gap-3 rounded-2xl border-2 border-solid p-4 transition-all duration-300 md:px-6 ${
                        wizardState.hasPrescription === 'no'
                          ? 'border-[#0788f1] bg-[#ebf8ff]'
                          : 'border-[#f1f7fa] [@media(hover:hover)]:hover:border-[#0788f1] [@media(hover:hover)]:hover:bg-[#f9fcfe]'
                      }`}
                    >
                      <span
                        className={`shrink-0 ${
                          wizardState.hasPrescription === 'no'
                            ? 'text-[#0788f1]'
                            : 'text-[#f1f7fa] [@media(hover:hover)]:group-hover:text-[#0788f1]'
                        }`}
                      >
                        <TreatIcon className="size-12" />
                      </span>
                      <div className="inline-flex flex-col items-start gap-1">
                        <span className="text-base leading-5 text-[#151515]">
                          Назначения нет
                        </span>
                        <span className="text-sm leading-5 text-[#151515] opacity-50">
                          Добавлю лекарства сам
                        </span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setWizardState((prev) => ({
                          ...prev,
                          hasPrescription: 'yes',
                          prescriptionType: 'paper',
                        }))
                      }
                      className={`group flex w-full flex-1 items-center gap-3 rounded-2xl border-2 border-solid p-4 transition-all duration-300 md:px-6 ${
                        wizardState.hasPrescription === 'yes'
                          ? 'border-[#0788f1] bg-[#ebf8ff]'
                          : 'border-[#f1f7fa] [@media(hover:hover)]:hover:border-[#0788f1] [@media(hover:hover)]:hover:bg-[#f9fcfe]'
                      }`}
                    >
                      <span
                        className={`shrink-0 ${
                          wizardState.hasPrescription === 'yes'
                            ? 'text-[#0788f1]'
                            : 'text-[#f1f7fa] [@media(hover:hover)]:group-hover:text-[#0788f1]'
                        }`}
                      >
                        <DoctorIcon className="size-12" />
                      </span>
                      <div className="inline-flex flex-col items-start gap-1">
                        <span className="text-base leading-5 text-[#151515]">
                          Есть назначение врача
                        </span>
                        <span className="text-sm leading-5 text-[#151515] opacity-50">
                          Загружу фото или файл
                        </span>
                      </div>
                    </button>
                  </div>

                  <div className="flex w-full items-start gap-4 md:w-[312px]">
                    <button
                      type="button"
                      onClick={() =>
                        setWizardState((prev) => ({
                          ...prev,
                          hasPrescription: 'unknown',
                        }))
                      }
                      className={`group flex w-full flex-1 items-center gap-3 rounded-2xl border-2 border-solid p-4 transition-all duration-300 md:px-6 ${
                        wizardState.hasPrescription === 'unknown'
                          ? 'border-[#0788f1] bg-[#ebf8ff]'
                          : 'border-[#f1f7fa] [@media(hover:hover)]:hover:border-[#0788f1] [@media(hover:hover)]:hover:bg-[#f9fcfe]'
                      }`}
                    >
                      <span
                        className={`shrink-0 ${
                          wizardState.hasPrescription === 'unknown'
                            ? 'text-[#0788f1]'
                            : 'text-[#f1f7fa] [@media(hover:hover)]:group-hover:text-[#0788f1]'
                        }`}
                      >
                        <BandageIcon className="size-[46px]" />
                      </span>
                      <div className="inline-flex flex-col items-start gap-2">
                        <span className="text-base leading-5 text-[#151515]">
                          Нужна помощь
                        </span>
                        <span className="text-sm leading-5 text-[#151515] opacity-50">
                          Опишу, что нужно
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-start gap-4">
              <SupportHint className="w-full" />

              {errors.hasPrescription && (
                <ErrorBlock message={errors.hasPrescription} />
              )}
              <button
                type="button"
                className="flex h-[60px] w-full items-center justify-center gap-2.5 self-stretch rounded-xl bg-[#0788f1] p-5 font-bold text-white transition-all duration-300 hover:bg-[#0670c9] focus:outline-none focus:ring-2 focus:ring-[#0788f1] focus:ring-offset-2 active:bg-[#055a9e]"
                onClick={goNext}
              >
                <span className="flex-1 text-center text-base leading-5">
                  Продолжить
                </span>
              </button>
            </div>
          </div>
        )}

        {active === 'type' && (
          <div className="flex h-fit w-full flex-1 flex-col items-center justify-between gap-4 rounded-[24px] bg-white p-[24px] md:p-[40px]">
            <div className="flex w-full flex-col items-start gap-[24px]">
              {wizardState.hasPrescription === 'unknown' ? (
                <>
                  <div className="w-full text-center text-2xl leading-8 text-[#151515]">
                    Опишите назначение
                  </div>
                  <p className="mb-[16px] w-full text-center text-base leading-6 text-[#151515] opacity-50">
                    Опишите в свободной форме, какие препараты вам нужны и как
                    вы планируете их принимать. Менеджер свяжется с вами для
                    уточнения деталей
                  </p>
                  <div className="flex w-full flex-col gap-[16px]">
                    <Textarea
                      wrapperClassName="mb-[40px]"
                      placeholder="Опишите нужные вам лекарства, дозировку и порядок приёма"
                      value={wizardState.description}
                      onChange={(e) =>
                        setWizardState((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                    {wizardState.prescriptionFiles?.length ? (
                      <div className="flex w-full flex-col gap-[8px]">
                        {wizardState.prescriptionFiles.map((file) => (
                          <UploadedFileItem
                            key={file.id}
                            fileName={file.fileName}
                            onDelete={() => {
                              setUploadError(null);
                              setWizardState((prev) => ({
                                ...prev,
                                prescriptionFiles: (
                                  prev.prescriptionFiles ?? []
                                ).filter((f) => f.id !== file.id),
                              }));
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : null}
                    {uploadError && <ErrorBlock message={uploadError} />}
                    {errors.description && (
                      <ErrorBlock message={errors.description} />
                    )}
                  </div>
                </>
              ) : (wizardState.hasPrescription === 'yes' ||
                  wizardState.hasPrescription === 'no') &&
                wizardState.prescriptionType === 'paper' ? (
                <div className="flex w-full flex-col items-start gap-[12px]">
                  {renderUploadZone('Загрузите фото вашего рецепта')}
                  <div
                    className="flex w-full items-start gap-[12px] rounded-[12px] p-[16px]"
                    style={{ background: '#e06ad414' }}
                  >
                    <WarnIcon className="shrink-0" />
                    <div className="flex flex-1 flex-col gap-[8px]">
                      <p className="text-sm leading-[1.125rem] text-[#151515]/50">
                        Если в вашем назначении есть рецептурные препараты и вы
                        загружаете только бумажный рецепт, заказ нужно будет
                        забрать самостоятельно в нашей аптеке по адресу:{' '}
                        <span className="leading-5">
                          Москва, ул. Алма-Атинская, д. 9, к. 2.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {wizardState.hasPrescription === 'yes' &&
            wizardState.prescriptionType === 'electronic' ? (
              <div className="flex w-full flex-col items-start gap-[24px]">
                <p className="w-full text-center text-base leading-6 text-[#151515]">
                  Для получения электронного рецепта авторизуйтесь через портал
                  госуслуг для загрузки данных или загрузите QR-код рецепта
                </p>
                <div className="flex w-full flex-col items-start gap-[24px]">
                  <button
                    className="flex h-[3.75rem] w-full items-center justify-center gap-[24px] rounded-[12px] border border-solid px-[20px] py-[16px]"
                    style={{ borderColor: '#0d4cd3' }}
                    type="button"
                  >
                    <span className="inline-flex items-center gap-[12px]">
                      <GosuslugiIcon className="h-[1.76rem] w-[70px]" />
                      <span className="text-lg font-semibold leading-6 text-[#0d4cd3]">
                        Загрузить электронный рецепт
                      </span>
                    </span>
                  </button>
                  <div
                    className="flex w-full cursor-pointer items-center justify-center gap-[12px] rounded-[12px] border border-dashed px-[20px] py-[32px]"
                    style={{
                      background: '#2c9aee14',
                      borderColor: '#2c9aee80',
                    }}
                    onClick={handleFileUploadClick}
                  >
                    <CameraIcon className="size-[28px]" />
                    <div className="text-base leading-5 text-[#2c9aee]">
                      {isUploading
                        ? 'Загрузка...'
                        : 'Загрузите фото вашего рецепта'}
                    </div>
                  </div>
                  {uploadError && <ErrorBlock message={uploadError} />}
                </div>
              </div>
            ) : null}

            <div className="flex w-full flex-col items-start gap-[16px]">
              {errors.prescriptionType && (
                <ErrorBlock message={errors.prescriptionType} />
              )}
              {errors.prescriptionImage && (
                <ErrorBlock message={errors.prescriptionImage} />
              )}
              {errors.orderFormationType && (
                <ErrorBlock message={errors.orderFormationType} />
              )}
              {wizardState.hasPrescription === 'unknown' && freeFormError && (
                <ErrorBlock message={freeFormError} />
              )}
              <SupportHint className="w-full" />
              <div className="flex w-full items-start gap-[16px]">
                <BackStepButton onClick={handlePrevStep} />
                <button
                  className="flex h-[3.75rem] flex-1 items-center justify-center gap-[24px] rounded-[12px] bg-[#0788f1] p-[20px] disabled:opacity-50"
                  type="button"
                  disabled={
                    !legalMutationsAllowed ||
                    (wizardState.hasPrescription === 'yes' &&
                      wizardState.prescriptionType !== 'select_from_list' &&
                      !wizardState.prescriptionFiles?.length) ||
                    (wizardState.hasPrescription === 'no' &&
                      !wizardState.prescriptionType) ||
                    (wizardState.hasPrescription === 'unknown' &&
                      (!wizardState.description.trim() || isCreatingFreeForm))
                  }
                  onClick={() => {
                    if (!legalMutationsAllowed) {
                      requireConsent();
                      return;
                    }
                    if (wizardState.hasPrescription === 'unknown') {
                      setFreeFormError(null);
                      createFreeFormReceipt({
                        patient_id: patientId,
                        text: wizardState.description,
                        ...(wizardState.prescriptionFiles?.length && {
                          scanned_receipts: wizardState.prescriptionFiles.map(
                            (f) => f.id,
                          ),
                        }),
                      });
                    } else {
                      goNext();
                    }
                  }}
                >
                  <div className="flex-1 text-center text-base font-bold leading-5 text-white">
                    {wizardState.hasPrescription === 'unknown' &&
                    isCreatingFreeForm
                      ? 'Отправка...'
                      : 'Продолжить'}
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {active === 'extra' && (
          // На мобилке отступ меньше остальных шагов: внутри редактор лекарств,
          // и при 24px строке «1 раз каждые 3 дня» уже не хватает ширины.
          <div className="flex w-full flex-1 flex-col items-start justify-between gap-8 rounded-[24px] bg-white p-4 md:p-[40px]">
            <div className="flex w-full flex-col items-start gap-8">
              {wizardState.hasPrescription === 'yes' ? (
                // С рецептом врача список необязателен: сюда докидывают своё
                // сверх назначенного, поэтому редактор прячем за «Да».
                <>
                  <div className="flex w-full flex-col items-start gap-[24px]">
                    <p className="w-full text-center text-2xl leading-8 text-[#151515]">
                      Хотите ли вы добавить что-то <br />к назначенным врачом
                      препаратам?
                    </p>
                    <div className="flex w-full items-center overflow-hidden rounded-[100px] bg-[#f7f8f9]">
                      {(['yes', 'no'] as const).map((value) => (
                        <button
                          key={value}
                          className={classNames(
                            'flex flex-1 items-center justify-center gap-[8px] rounded-[100px] p-[16px]',
                            wizardState.addMore === value
                              ? 'bg-[#151515] text-[#f6f8f9]'
                              : 'bg-[#f6f8f9] text-[#151515]/50',
                          )}
                          onClick={() =>
                            setWizardState((prev) => ({
                              ...prev,
                              addMore: value,
                            }))
                          }
                          type="button"
                        >
                          {value === 'yes' ? 'Да' : 'Нет'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {wizardState.addMore === 'yes' && medicinesEditor}
                </>
              ) : (
                <>
                  <div className="flex w-full flex-col items-start gap-2">
                    <div className="w-full text-center text-2xl font-normal leading-8 text-[#151515]">
                      Заполните, как принимать лекарство
                    </div>
                    <div className="w-full text-center text-base font-normal leading-5 text-[#151515] opacity-50">
                      Мы автоматически разложим его по индивуальным пакетикам
                    </div>
                  </div>
                  {medicinesEditor}
                  <div className="flex w-full flex-col items-start gap-3">
                    <p className="w-full text-base font-normal leading-5 text-[#151515] opacity-50">
                      Проще написать от руки? Прикрепите фото списка лекарств —
                      выбирать из каталога тогда не обязательно.
                    </p>
                    {renderUploadZone('Прикрепить фото списка лекарств')}
                  </div>
                </>
              )}
            </div>

            <div className="flex w-full flex-col items-start gap-4">
              {hasAttemptedExtra && errors.additionalMedicines && (
                <ErrorBlock message={errors.additionalMedicines} />
              )}
              <SupportHint className="w-full" />
              <div className="flex w-full items-start gap-4">
                <BackStepButton onClick={handlePrevStep} />
                <button
                  className="flex h-[60px] flex-1 items-center justify-center gap-2.5 rounded-xl bg-[#0788f1] p-5"
                  type="button"
                  onClick={() => {
                    void handleExtraNext();
                  }}
                >
                  <span className="flex-1 text-center text-base font-bold leading-5 text-white">
                    Продолжить
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {active === 'data' && (
          <div className="flex w-full flex-1 flex-col items-start justify-between gap-8 rounded-b-[24px] bg-white p-6 md:gap-[40px] md:p-[40px]">
            <div className="inline-flex w-full flex-col items-start gap-[24px]">
              <div className="w-full text-center text-2xl leading-8 text-[#151515]">
                Оформление заказа
              </div>
              <div className="flex w-full flex-col items-start gap-[24px]">
                <div className="flex w-full items-center overflow-hidden rounded-[100px] bg-[#f7f8f9]">
                  <button
                    className={`flex flex-1 items-center justify-center gap-[8px] rounded-[100px] p-[16px] ${
                      wizardState.recipient === 'self'
                        ? 'bg-[#151515] text-[#f6f8f9]'
                        : 'bg-[#f6f8f9] text-[#151515]/50'
                    }`}
                    onClick={() =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipient: 'self',
                      }))
                    }
                    type="button"
                  >
                    Себе
                  </button>
                  <button
                    className={`flex flex-1 items-center justify-center gap-[8px] rounded-[100px] p-[16px] ${
                      wizardState.recipient === 'other'
                        ? 'bg-[#151515] text-[#f6f8f9]'
                        : 'bg-[#f6f8f9] text-[#151515]/50'
                    }`}
                    onClick={() =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipient: 'other',
                      }))
                    }
                    type="button"
                  >
                    Другому
                  </button>
                </div>
              </div>
            </div>

            {wizardState.recipient !== 'self' && (
              <div className="flex w-full flex-col items-center gap-[24px]">
                <div className="w-full text-center text-2xl leading-8 text-[#151515]">
                  Данные пациента
                </div>
                <div className="flex w-full flex-col items-start gap-[20px]">
                  <input
                    type="text"
                    placeholder="Фамилия"
                    value={wizardState.recipientData?.surname || ''}
                    onChange={(e) =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipientData: {
                          ...prev.recipientData,
                          surname: e.target.value,
                          name: prev.recipientData?.name || '',
                        },
                      }))
                    }
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] px-[20px] py-[18px] outline-none transition focus-within:border focus-within:border-[#0788f1] hover:border-[#0788f1]"
                  />
                  <input
                    type="text"
                    placeholder="Имя"
                    value={wizardState.recipientData?.name || ''}
                    onChange={(e) =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipientData: {
                          ...prev.recipientData,
                          name: e.target.value,
                          surname: prev.recipientData?.surname || '',
                        },
                      }))
                    }
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] px-[20px] py-[18px] outline-none transition focus-within:border focus-within:border-[#0788f1] hover:border-[#0788f1]"
                  />
                  <input
                    type="text"
                    placeholder="Отчество"
                    value={wizardState.recipientData?.midname || ''}
                    onChange={(e) =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipientData: {
                          ...prev.recipientData,
                          midname: e.target.value,
                          name: prev.recipientData?.name || '',
                          surname: prev.recipientData?.surname || '',
                        },
                      }))
                    }
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] px-[20px] py-[18px] outline-none transition focus-within:border-[1.6px] focus-within:border-[#0788f1] hover:border-[#0788f1]"
                  />
                  <IMaskInput
                    mask="00.00.0000"
                    lazy={false}
                    overwrite
                    inputMode="numeric"
                    value={wizardState.recipientData?.birthdate || ''}
                    onAccept={(value: string) =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipientData: {
                          ...prev.recipientData,
                          birthdate: value,
                          name: prev.recipientData?.name || '',
                          surname: prev.recipientData?.surname || '',
                        },
                      }))
                    }
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] bg-transparent px-[20px] py-[18px] text-base font-normal leading-5 text-[#151515] outline-none transition placeholder:text-[#151515]/50 focus-within:border-[1.6px] focus-within:border-[#0788f1] hover:border-[#0788f1]"
                    placeholder="ДД.ММ.ГГГГ"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={wizardState.recipientData?.email || ''}
                    onChange={(e) =>
                      setWizardState((prev) => ({
                        ...prev,
                        recipientData: {
                          ...prev.recipientData,
                          email: e.target.value,
                          name: prev.recipientData?.name || '',
                          surname: prev.recipientData?.surname || '',
                        },
                      }))
                    }
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] px-[20px] py-[18px] outline-none transition focus-within:border-[1.6px] focus-within:border-[#0788f1] hover:border-[#0788f1]"
                  />
                  <input
                    type="text"
                    placeholder="Адрес проживания"
                    className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-solid border-[#afc4d1] px-[20px] py-[18px] text-[#151515]/50 outline-none transition focus-within:border-[1.6px] focus-within:border-[#0788f1] hover:border-[#0788f1]"
                    disabled
                  />
                </div>
              </div>
            )}

            <div className="flex w-full flex-col items-start gap-[16px]">
              <div className="flex w-full items-start gap-[16px]">
                <div className="flex flex-1 items-baseline gap-[8px]">
                  <div className="text-xl leading-[1.875rem] text-[#151515]">
                    Адреса
                  </div>
                  {addresses.items && addresses.items.length > 0 && (
                    <div className="text-sm leading-5 text-[#151515]/40">
                      (у вас {addresses.items.length})
                    </div>
                  )}
                </div>
              </div>
              <div className="flex w-full flex-col items-start gap-[16px]">
                <LineIcon className="h-px w-full" />
                {addresses.items && addresses.items.length > 0 ? (
                  addresses.items.map((address) => (
                    <button
                      key={address.id}
                      type="button"
                      className="flex w-full items-center justify-start gap-[2px] py-[8px] text-left"
                      onClick={() =>
                        setWizardState((prev) => ({
                          ...prev,
                          addressId: address.id,
                        }))
                      }
                    >
                      <div className="flex flex-1 flex-col items-start justify-center gap-[8px]">
                        <div className="flex w-full items-center gap-[12px]">
                          <div
                            className={`flex size-[20px] shrink-0 items-center justify-center rounded-[50%] border-2 ${
                              wizardState.addressId === address.id
                                ? 'border-[#0788f1] bg-[#0788f1]'
                                : 'border-[#afc4d1]'
                            }`}
                          >
                            {wizardState.addressId === address.id && (
                              <svg
                                className="size-[12px]"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2.5 6.2L4.7 8.4L9.5 3.6"
                                  stroke="white"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="inline-flex items-center gap-[8px]">
                            <p className="text-base leading-6 text-[#151515]">
                              {address.address_text}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="py-[16px] text-center text-[#151515]/50">
                    Нет адресов
                  </div>
                )}
                <LineIcon className="h-px w-full" />
              </div>
              <button
                type="button"
                className={`inline-flex items-center gap-[12px] ${
                  legalMutationsAllowed ? '' : 'cursor-not-allowed opacity-50'
                }`}
                aria-disabled={!legalMutationsAllowed}
                onClick={() => {
                  if (!legalMutationsAllowed) {
                    requireConsent();
                    return;
                  }
                  setIsAddressModalOpen(true);
                }}
              >
                <div className="inline-flex items-start gap-[24px] rounded-[8px] bg-[#ecf7fe] p-[6px]">
                  <svg
                    className="size-[16px]"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 3V13M3 8H13"
                      stroke="#0788F1"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="text-base leading-6 text-[#151515]">
                  Добавить адрес
                </div>
              </button>
            </div>

            <div className="flex w-full flex-col items-start gap-[16px]">
              {errors.addressId && <ErrorBlock message={errors.addressId} />}
              <SupportHint className="w-full" />
              <div className="flex w-full items-start gap-[16px]">
                <BackStepButton onClick={handlePrevStep} />
                <button
                  className="flex h-[3.75rem] flex-1 items-center justify-center gap-[24px] rounded-[12px] bg-[#0788f1] p-[20px] disabled:opacity-50"
                  type="button"
                  disabled={
                    !legalMutationsAllowed ||
                    orderSubmitted ||
                    isCreatingReceipt ||
                    isConfirmingOrder ||
                    !wizardState.addressId ||
                    (!wizardState.freeFormReceiptId && !defaultPaymentMethodId)
                  }
                  onClick={() => {
                    if (!legalMutationsAllowed) {
                      requireConsent();
                      return;
                    }
                    if (orderSubmitted) return;
                    if (validateStep('data')) {
                      setOrderSubmitted(true);
                      // Если есть free-form рецепт, создаем заказ сразу
                      if (
                        wizardState.freeFormReceiptId &&
                        wizardState.addressId
                      ) {
                        confirmOrder({
                          receiptId: wizardState.freeFormReceiptId,
                          addressId: wizardState.addressId,
                          receiptType: 'free_form_receipt',
                        });
                      } else {
                        // Иначе создаем обычный рецепт и заказ
                        handleCreateOrder();
                      }
                    }
                  }}
                >
                  <div className="flex-1 text-center text-base font-bold leading-5 text-white">
                    {isCreatingReceipt || isConfirmingOrder
                      ? 'Создание заказа...'
                      : 'Оформить заказ'}
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {active === 'summary' && (
          <div className="flex w-full flex-1 flex-col items-start justify-between gap-8 rounded-b-[24px] bg-white p-6 md:gap-[40px] md:p-[40px]">
            <div className="flex w-full flex-col items-start gap-[40px]">
              {/* Показываем рецепт, если он был загружен на любом шаге */}
              {wizardState.prescriptionFiles?.length ? (
                <div className="flex w-full flex-col items-start gap-[24px]">
                  <div className="w-full text-center text-2xl leading-8 text-[#151515]">
                    Ваш рецепт
                  </div>
                  <div
                    className="flex w-full flex-col gap-[8px] rounded-[12px] border border-solid px-[20px] py-[16px]"
                    style={{
                      background: '#5fd98e14',
                      borderColor: '#e5f9ed',
                    }}
                  >
                    {wizardState.prescriptionFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-[12px] text-base leading-5 text-[#151515]/50"
                      >
                        <CheckIcon className="shrink-0" />
                        {file.fileName}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Показываем список лекарств, если они были добавлены */}
              {wizardState.additionalMedicines.length > 0 && (
                <div className="flex w-full flex-col items-start gap-[24px]">
                  <div className="w-full text-center text-2xl leading-8 text-[#151515]">
                    Ваш заказ
                  </div>

                  <div className="flex w-full flex-col items-start">
                    <div className="flex w-full items-start gap-3 pb-[12px] pt-0 md:gap-[16px]">
                      <div className="min-w-0 flex-1 text-sm leading-5 text-[#151515]/40">
                        Препарат
                      </div>
                      <div className="min-w-0 flex-1 text-sm leading-5 text-[#151515]/40">
                        Частота приёма
                      </div>
                      <div className="w-20 shrink-0 text-right text-sm leading-5 text-[#151515]/40 md:w-[6.25rem]">
                        Цена
                      </div>
                    </div>
                    <LineIcon className="h-px w-full" />

                    {wizardState.additionalMedicines.map((medicine, idx) => {
                      // Пока расчёт не пришёл — прочерк; если бэкенд недоступен,
                      // показываем прикидку по цене из карточки препарата.
                      const estimatedPrice =
                        medicine.medicine.medicine_price *
                        medicine.count *
                        medicine.duration;
                      const price =
                        orderPreview?.rows[idx]?.price ??
                        (isPreviewError ? estimatedPrice : null);
                      // «1 таблетка в день» — доза за период частоты, а не
                      // общее количество таблеток на весь курс.
                      const dosePerPeriod = medicine.time.reduce(
                        (sum, time) => sum + (time.count || 0),
                        0,
                      );
                      return (
                        <div
                          key={idx}
                          className="flex w-full items-start gap-3 px-0 py-[12px] md:gap-[16px]"
                        >
                          <div className="flex min-w-0 flex-1 flex-col items-start gap-[2px]">
                            <div className="w-full break-words text-sm leading-5 text-[#151515] md:text-base">
                              {medicine.medicine.name}
                            </div>
                            <div className="text-sm leading-5 text-[#151515]/50">
                              x{medicine.totalCount}
                            </div>
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col items-start gap-[2px]">
                            <div className="w-full break-words text-sm leading-5 text-[#151515] md:text-base">
                              {dosePerPeriod}{' '}
                              {formatMedicinePlural(dosePerPeriod)}{' '}
                              {medicine.frequency.value.toLowerCase()}
                            </div>
                            <div className="text-sm leading-5 text-[#151515]/50">
                              {formatIntakeTimes(medicine.time)}
                            </div>
                          </div>
                          <div className="flex w-20 shrink-0 flex-col items-end gap-[24px] md:w-[6.25rem]">
                            <div className="flex w-full items-center justify-end gap-[2px]">
                              <div className="text-right text-sm leading-5 text-[#151515] md:text-base">
                                {price != null
                                  ? formatMoney(price)
                                  : isPreviewFetching
                                  ? '…'
                                  : '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <LineIcon className="h-px w-full" />

                    {orderPreview && (
                      <div className="flex w-full flex-col items-start gap-[8px] pt-[16px]">
                        <TotalRow
                          label="Лекарства"
                          value={formatMoney(orderPreview.medicinesTotal)}
                        />
                        <TotalRow
                          label="Доставка"
                          value={formatMoney(orderPreview.deliveryPrice)}
                        />
                        <TotalRow
                          label="Сборка заказа"
                          value={formatMoney(orderPreview.packageFee)}
                        />
                        <TotalRow
                          label="Итого"
                          value={formatMoney(orderPreview.total)}
                          strong
                        />
                      </div>
                    )}
                  </div>

                  {isPreviewError && (
                    <ErrorBlock message="Не удалось получить актуальные цены — показана примерная стоимость. Итоговую сумму подтвердим при оформлении." />
                  )}

                  {orderPreview &&
                    orderPreview.medicinesWithoutPrice.length > 0 && (
                      <ErrorBlock
                        message={`Не нашли актуальную цену: ${orderPreview.medicinesWithoutPrice
                          .map((item) => item.medicine_name)
                          .join(
                            ', ',
                          )}. В расчёт они не вошли — стоимость уточнит менеджер.`}
                      />
                    )}
                </div>
              )}
              <div className="flex size-full flex-col gap-[16px]">
                <div className="text-xl leading-[1.875rem] text-[#151515]">
                  Комментарий к заказу
                </div>
                <Textarea
                  minHeight="min-h-[100px]"
                  placeholder="Введите комментарий (необязательно)"
                  value={wizardState.orderComment || ''}
                  onChange={(e) =>
                    setWizardState((prev) => ({
                      ...prev,
                      orderComment: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex w-full flex-col items-start gap-[16px]">
              {errors.summary && <ErrorBlock message={errors.summary} />}
              <SupportHint className="w-full" />
              <div className="flex w-full items-start gap-[16px]">
                <BackStepButton onClick={handlePrevStep} />

                <button
                  className="flex h-[3.75rem] flex-1 items-center justify-center gap-[24px] rounded-[12px] bg-[#0788f1] p-[20px] disabled:opacity-50"
                  type="button"
                  disabled={
                    !legalMutationsAllowed ||
                    (wizardState.hasPrescription === 'yes' &&
                      wizardState.additionalMedicines.length === 0 &&
                      !wizardState.prescriptionFiles?.length)
                  }
                  onClick={() => {
                    if (!legalMutationsAllowed) {
                      requireConsent();
                      return;
                    }
                    goNext();
                  }}
                >
                  <div className="flex-1 text-center text-base font-bold leading-5 text-white">
                    Продолжить
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Один скрытый input на весь визард: загрузка используется и на шаге
            «Тип», и на шаге «Заказ» в сценарии без назначения. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />

        <CreateAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          patientId={profile?.people_main_id || null}
          onAddressSelect={handleAddressSelect}
        />
      </div>
    </div>
  );
};
